import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BusyButton, ConfirmDialog, DataTable, PageHeader, RowMenu, SearchSelect, Switch, useToast } from "../../components/ui";
import { ErrorState, Skeleton } from "../../components/States";
import { formatDateTime } from "../../lib/format";
import { useAuth } from "../../auth/store";
import { staffApi, staffKeys } from "./api";
import { useDebounced } from "./customers";
import "./staff.css";

type Signup = { email: string; createdAt: string; notifiedAt?: string | null };
type Confirm = { kind: "email" | "delete"; emails: string[] };

function domainOf(email: string): string {
  return email.split("@")[1]?.toLowerCase() ?? "";
}

function people(n: number): string {
  return n === 1 ? "1 person" : `${n} people`;
}

export function StaffAvailabilityPage() {
  const auth = useAuth();
  const owner = auth.me?.user.staffRole === "owner";
  const qc = useQueryClient();
  const toast = useToast();
  const product = useQuery({ queryKey: staffKeys.product, queryFn: staffApi.product });
  const [pending, setPending] = useState<boolean | null>(null);
  const [qInput, setQInput] = useState("");
  const [domain, setDomain] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const typed = qInput.trim().toLowerCase();
  const debounced = useDebounced(typed, 250);
  const q = typed === "" ? "" : debounced;
  const allBox = useRef<HTMLInputElement>(null);

  const save = useMutation({
    mutationFn: (open: boolean) => staffApi.setProduct(open),
    onSuccess: (next) => {
      qc.setQueryData(staffKeys.product, next);
      setPending(null);
      toast.push("success", next.open ? "The product is open." : "The product is closed.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const email = useMutation({
    mutationFn: (emails: string[]) => staffApi.emailLaunch(emails),
    onSuccess: (next, emails) => {
      qc.setQueryData(staffKeys.product, next);
      setSelected((cur) => {
        const nextSet = new Set(cur);
        for (const address of emails) nextSet.delete(address);
        return nextSet;
      });
      setConfirm(null);
      const failed = next.failed ?? 0;
      const sent = next.sent ?? 0;
      toast.push(failed > 0 ? "info" : "success", failed > 0 ? `Emailed ${people(sent)}. ${failed} could not be sent.` : `Emailed ${people(sent)}.`);
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const remove = useMutation({
    mutationFn: (emails: string[]) => staffApi.deleteNotify(emails),
    onSuccess: (next, emails) => {
      qc.setQueryData(staffKeys.product, next);
      setSelected((cur) => {
        const nextSet = new Set(cur);
        for (const address of emails) nextSet.delete(address);
        return nextSet;
      });
      setConfirm(null);
      toast.push("success", `Deleted ${people(emails.length)}.`);
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const open = product.data?.open ?? true;
  const signups = product.data?.signups ?? [];
  const domains = useMemo(() => {
    const found = new Set<string>();
    for (const row of signups) {
      const domainName = domainOf(row.email);
      if (domainName) found.add(domainName);
    }
    return [...found].sort();
  }, [signups]);
  const filtered = useMemo(
    () => signups.filter((row) => (!q || row.email.toLowerCase().includes(q)) && (!domain || domainOf(row.email) === domain)),
    [signups, q, domain],
  );
  const visibleSelected = filtered.filter((row) => selected.has(row.email));
  const allSelected = filtered.length > 0 && visibleSelected.length === filtered.length;
  const someSelected = visibleSelected.length > 0 && !allSelected;

  useEffect(() => {
    if (allBox.current) allBox.current.indeterminate = someSelected;
  }, [someSelected]);

  useEffect(() => {
    const known = new Set(signups.map((row) => row.email));
    setSelected((cur) => {
      const nextSet = new Set([...cur].filter((address) => known.has(address)));
      return nextSet.size === cur.size ? cur : nextSet;
    });
  }, [signups]);

  const toggleAll = () =>
    setSelected((cur) => {
      const nextSet = new Set(cur);
      for (const row of filtered) {
        if (allSelected) nextSet.delete(row.email);
        else nextSet.add(row.email);
      }
      return nextSet;
    });
  const toggle = (address: string) =>
    setSelected((cur) => {
      const nextSet = new Set(cur);
      if (nextSet.has(address)) nextSet.delete(address);
      else nextSet.add(address);
      return nextSet;
    });
  const ask = (kind: Confirm["kind"], emails: string[]) => {
    if (emails.length) setConfirm({ kind, emails });
  };
  const clearFilters = () => {
    setQInput("");
    setDomain("");
  };
  const narrowed = !!(q || domain);
  const busy = email.isPending || remove.isPending;

  const columns = [
    ...(owner
      ? [
          {
            key: "check",
            header: <input ref={allBox} type="checkbox" aria-label="Select everyone shown" checked={allSelected} onChange={toggleAll} autoComplete="off" />,
            width: 36,
            className: "launch-check",
            render: (row: Signup) => (
              <input
                type="checkbox"
                aria-label={`Select ${row.email}`}
                checked={selected.has(row.email)}
                onChange={() => toggle(row.email)}
                autoComplete="off"
              />
            ),
          },
        ]
      : []),
    {
      key: "email",
      header: "Email",
      label: "Email",
      className: "launch-email",
      render: (row: Signup) => <strong>{row.email}</strong>,
    },
    {
      key: "status",
      header: "Status",
      label: "Status",
      className: "launch-status",
      render: (row: Signup) => (row.notifiedAt ? <span className="badge ok">Emailed</span> : <span className="badge neutral">Waiting</span>),
    },
    {
      key: "when",
      header: "Added",
      label: "Added",
      className: "launch-added",
      render: (row: Signup) => (
        <time className="muted small" dateTime={row.notifiedAt ?? row.createdAt}>
          {row.notifiedAt ? `Emailed ${formatDateTime(row.notifiedAt)}` : formatDateTime(row.createdAt)}
        </time>
      ),
    },
    ...(owner
      ? [
          {
            key: "act",
            header: "",
            align: "right" as const,
            className: "launch-actions",
            render: (row: Signup) => (
              <RowMenu label={`Actions for ${row.email}`}>
                {open && (
                  <button type="button" role="menuitem" onClick={() => ask("email", [row.email])}>
                    {row.notifiedAt ? "Email again" : "Email"}
                  </button>
                )}
                <button type="button" role="menuitem" className="danger" onClick={() => ask("delete", [row.email])}>
                  Delete
                </button>
              </RowMenu>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <PageHeader title="Availability" subtitle="Close the product while it is still coming soon. You can still sign in." />
      {product.isPending ? (
        <Skeleton lines={4} />
      ) : product.isError ? (
        <ErrorState error={product.error} retry={() => void product.refetch()} />
      ) : (
        <div className="launch-page">
          <div className="card section">
            <Switch
              checked={open}
              disabled={!owner || save.isPending}
              onChange={(next) => setPending(next)}
              label="Product is open"
              hint={
                owner
                  ? "When this is off, the site says Backstory is coming soon. People can leave an email. They cannot create an account or open the product."
                  : "Only the platform owner can change this."
              }
            />
          </div>
          <div className="card section">
            <h2>People to notify</h2>
            {signups.length > 0 && (
              <div className="toolbar launch-filters" aria-label="Notify list filters">
                <input
                  className="input"
                  type="search"
                  placeholder="Search by email"
                  aria-label="Search by email"
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  autoComplete="off"
                />
                <SearchSelect
                  value={domain}
                  options={[{ value: "", label: "All domains" }, ...domains.map((name) => ({ value: name, label: name }))]}
                  onChange={setDomain}
                  aria-label="Domain"
                  searchPlaceholder="Search domains…"
                />
              </div>
            )}
            {owner && selected.size > 0 && (
              <div className="launch-bulk" role="toolbar" aria-label="Selected people">
                <strong>{selected.size} selected</strong>
                <BusyButton className="btn primary" busy={email.isPending} busyLabel="Sending…" disabled={!open || busy} onClick={() => ask("email", [...selected])}>
                  Email
                </BusyButton>
                <BusyButton className="btn danger" busy={remove.isPending} busyLabel="Deleting…" disabled={busy} onClick={() => ask("delete", [...selected])}>
                  Delete
                </BusyButton>
                <button type="button" className="btn ghost" onClick={() => setSelected(new Set())}>
                  Clear
                </button>
                {!open && <span className="muted small">Open the product before emailing.</span>}
              </div>
            )}
            {signups.length === 0 ? (
              <p className="muted">No one has asked to be notified yet.</p>
            ) : (
              <div className="stack-table launch-table">
                <DataTable
                  ariaLabel="People to notify"
                  rows={filtered}
                  rowKey={(row) => row.email}
                  columns={columns}
                  empty={
                    narrowed ? (
                      <span>
                        No one matches.{" "}
                        <button type="button" className="btn ghost sm" onClick={clearFilters}>
                          Clear filters
                        </button>
                      </span>
                    ) : (
                      "No one has asked to be notified yet."
                    )
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}
      <ConfirmDialog
        open={pending !== null}
        danger={pending === false}
        typeToConfirm={false}
        title={pending ? "Open the product" : "Close the product"}
        confirmLabel={pending ? "Open the product" : "Close the product"}
        busy={save.isPending}
        onClose={() => {
          if (!save.isPending) setPending(null);
        }}
        onConfirm={() => {
          if (pending !== null) save.mutate(pending);
        }}
      >
        {pending
          ? "People will be able to create an account and use Backstory."
          : "People will see a coming soon page and can leave an email. They will not be able to create an account or open the product."}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === "email"}
        title={confirm?.emails.length === 1 ? "Email this person" : "Email selected"}
        confirmLabel="Send email"
        busy={email.isPending}
        onClose={() => {
          if (!email.isPending) setConfirm(null);
        }}
        onConfirm={() => confirm && email.mutate(confirm.emails)}
      >
        <p style={{ margin: 0 }}>
          {confirm?.emails.length === 1
            ? `${confirm.emails[0]} will get an email that Backstory is open, with a link to create an account.`
            : `${people(confirm?.emails.length ?? 0)} will get an email that Backstory is open, with a link to create an account.`}
        </p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === "delete"}
        danger
        title={confirm?.emails.length === 1 ? "Delete from the list" : "Delete selected"}
        confirmLabel={confirm?.emails.length === 1 ? "Delete" : "Delete selected"}
        busy={remove.isPending}
        onClose={() => {
          if (!remove.isPending) setConfirm(null);
        }}
        onConfirm={() => confirm && remove.mutate(confirm.emails)}
      >
        <p style={{ margin: 0 }}>
          {confirm?.emails.length === 1
            ? `${confirm.emails[0]} will be removed from the list.`
            : `${people(confirm?.emails.length ?? 0)} will be removed from the list.`}
        </p>
      </ConfirmDialog>
    </div>
  );
}
